import { ArrowForward, Celebration, Dashboard, Group, Person, Settings } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EGender } from '@sharedInterfaces/IEmployee';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import updateMyDescription from '@src/APIs/graphQl/Employee/updateMyDescription';
import updateMyGenderAndName from '@src/APIs/graphQl/Employee/updateMyGenderAndName';
import changeMapRole from '@src/APIs/graphQl/Role/changeMapRole';
import {
    setEmployeeDescription,
    setEmployeeNames,
    setEmployeeRoles,
    setEmployeeSkills,
} from '@store/reducer/employeeReducer';
import { setFirstExperience } from '@store/reducer/employeeSettingsReducer';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { OnboardingData } from '../types/OnboardingTypes';

interface CompletionStepProps {
    data: OnboardingData;
    onComplete: () => void;
    setAgentMessage: (message: string) => void;
}

export default function CompletionStep({ data, onComplete, setAgentMessage }: CompletionStepProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    const NEXT_STEPS = [
        {
            icon: <Person />,
            title: langStrings.onboarding.completeProfile,
            description: langStrings.onboarding.completeProfileDescription,
            action: langStrings.onboarding.toProfile,
            link: '/profile/edit',
        },
        {
            icon: <Group />,
            title: langStrings.onboarding.meetTeam,
            description: langStrings.onboarding.meetTeamDescription,
            action: langStrings.onboarding.viewTeam,
            link: '/people-org/employee',
        },
        {
            icon: <Dashboard />,
            title: langStrings.onboarding.exploreDashboard,
            description: langStrings.onboarding.exploreDashboardDescription,
            action: langStrings.onboarding.toDashboard,
            link: '/home',
        },
        {
            icon: <Settings />,
            title: langStrings.onboarding.adjustSettings,
            description: langStrings.onboarding.adjustSettingsDescription,
            action: langStrings.onboarding.settings,
            link: '/settings',
        },
    ];

    useEffect(() => {
        setAgentMessage(
            langStrings.onboarding.agentCompletionMessage.replace('{firstName}', data.personalInfo.firstName),
        );

        // Save data to store and backend
        const saveOnboardingData = async () => {
            try {
                // Update personal info
                if (data.personalInfo.firstName && data.personalInfo.lastName) {
                    await updateMyGenderAndName(data.personalInfo.firstName, data.personalInfo.lastName, EGender.OTHER);
                    dispatch(
                        setEmployeeNames({
                            firstname: data.personalInfo.firstName,
                            lastname: data.personalInfo.lastName,
                        }),
                    );
                }

                if (data.personalInfo.shortDescription) {
                    await updateMyDescription(data.personalInfo.shortDescription);
                    dispatch(setEmployeeDescription(data.personalInfo.shortDescription));
                }

                // Update skills in store
                const skillsForStore = data.selectedItems.skills.map((skill) => ({
                    id: skill.id,
                    title: skill.name,
                    level: skill.level,
                }));
                dispatch(setEmployeeSkills(skillsForStore));

                // Update roles
                for (const role of data.selectedItems.roles) {
                    await changeMapRole(role.id, true);
                }
                const rolesForStore = data.selectedItems.roles.map((role) => ({
                    id: role.id,
                    title: role.name,
                }));
                dispatch(setEmployeeRoles(rolesForStore));

                // Mark first experience as completed
                dispatch(setFirstExperience(true));

                console.log('Onboarding data saved successfully');
            } catch (error) {
                console.error('Error saving onboarding data:', error);
            }
        };

        saveOnboardingData();
    }, [data, setAgentMessage, dispatch, langStrings.onboarding.agentCompletionMessage.replace]);

    const handleQuickAction = (link: string) => {
        onComplete();
        navigate(link);
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            {/* Success Animation */}
            <Box sx={{ mb: 4 }}>
                <Avatar
                    sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 3,
                        background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.primary.main} 90%)`,
                        animation: 'bounce 2s ease-in-out infinite',
                        '@keyframes bounce': {
                            '0%, 20%, 50%, 80%, 100%': {
                                transform: 'translateY(0)',
                            },
                            '40%': {
                                transform: 'translateY(-10px)',
                            },
                            '60%': {
                                transform: 'translateY(-5px)',
                            },
                        },
                    }}
                >
                    <Celebration sx={{ fontSize: 60 }} />
                </Avatar>

                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: theme.palette.success.main }}>
                    {langStrings.onboarding.welcomeToTeam}
                </Typography>

                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                    {langStrings.onboarding.onboardingCompleted}
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                    {langStrings.onboarding.profileCreatedNotification}
                </Typography>
            </Box>

            {/* Summary */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    background: alpha(theme.palette.success.main, 0.05),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        sx={{ fontWeight: 600, mb: 3, color: theme.palette.success.main }}
                    >
                        {langStrings.onboarding.onboardingResult}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                {data.selectedItems.skills.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {langStrings.onboarding.skills}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                                {data.selectedItems.superPowers?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {langStrings.onboarding.superPowers}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                                {data.selectedItems.certificates.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {langStrings.onboarding.certificates}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                                {data.selectedItems.roles.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {langStrings.onboarding.roles}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Notification Status */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    background: alpha(theme.palette.info.main, 0.05),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            sx={{ fontWeight: 600, color: theme.palette.info.main }}
                        >
                            {langStrings.onboarding.teamLeaderNotified}
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        {langStrings.onboarding.teamLeaderNotificationDescription}
                    </Typography>
                </CardContent>
            </Card>

            {/* Next Steps */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: alpha(theme.palette.background.paper, 0.7),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        sx={{ fontWeight: 600, mb: 3, textAlign: 'left' }}
                    >
                        {langStrings.onboarding.recommendedNextSteps}
                    </Typography>

                    <List sx={{ p: 0 }}>
                        {NEXT_STEPS.map((step) => (
                            <ListItem
                                key={step.title}
                                sx={{
                                    px: 0,
                                    py: 1.5,
                                    borderRadius: 2,
                                    alignItems: 'flex-start',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56, mt: 0.5 }}>
                                    <Avatar
                                        sx={{
                                            width: isMobile ? 32 : 40,
                                            height: isMobile ? 32 : 40,
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {React.cloneElement(step.icon, { sx: { fontSize: isMobile ? 16 : 24 } })}
                                    </Avatar>
                                </ListItemIcon>
                                <Box sx={{ flex: 1 }}>
                                    <ListItemText
                                        primary={step.title}
                                        secondary={step.description}
                                        primaryTypographyProps={{ fontWeight: 600 }}
                                    />
                                    {isMobile && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowForward />}
                                            onClick={() => handleQuickAction(step.link)}
                                            sx={{
                                                mt: 1,
                                                px: 1,
                                                fontSize: '0.75rem',
                                                '&:hover': {
                                                    bgcolor: theme.palette.primary.main,
                                                    color: 'white',
                                                },
                                            }}
                                        >
                                            {step.action}
                                        </Button>
                                    )}
                                </Box>
                                {!isMobile && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={<ArrowForward />}
                                        onClick={() => handleQuickAction(step.link)}
                                        sx={{
                                            ml: 2,
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.main,
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        {step.action}
                                    </Button>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* Final Action */}
            <Box sx={{ textAlign: 'center' }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={() => {
                        onComplete();
                        navigate('/dashboard');
                    }}
                    startIcon={<Dashboard />}
                    sx={{
                        px: isMobile ? 2 : 4,
                        py: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '0.875rem' : '1.1rem',
                        fontWeight: 600,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    {langStrings.onboarding.toDashboard}
                </Button>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {langStrings.onboarding.successInNewRole}
                </Typography>
            </Box>
        </Box>
    );
}
